// `POST /api/visitor` — the one cookie retells sets that exists purely so ad
// attribution survives Safari.
//
// THE FAILURE THIS CLOSES. `leadId` and the last-touch click id lived only in
// `localStorage`. WebKit's Intelligent Tracking Prevention deletes ALL
// script-writable storage — `localStorage`, IndexedDB, service-worker
// registrations, and cookies written via `document.cookie` — for a site the
// visitor reached by a cross-site navigation to a URL carrying link-decoration
// params, once they have not interacted with it for a stretch of days. A Google
// ad click is EXACTLY that shape: `google.com` → `retells…/?gclid=…`. So the
// click id was being wiped for precisely the traffic it exists to serve, and
// against an 89-day offline-conversion upload window: the sale then settled
// silently as `no_click_id` and the ad group that paid for it was bid down.
//
// WHY A COOKIE IS THE FIX. WebKit caps storage by CREATION METHOD, not by
// readability. A cookie delivered in a `Set-Cookie` HTTP header from a genuine
// first-party response keeps its declared `Max-Age`, HttpOnly or not; one
// written from JS by `document.cookie` does not. The exact cap on the
// script-set kind has moved across WebKit releases and is not worth encoding
// here — the only load-bearing claim is the asymmetry itself: server-set
// survives, script-set lives as long as WebKit currently decides. That is why
// this is a worker route and not four lines in `attribution.ts`.
//
// WHY THE CLIENT SENDS THE ID RATHER THAN THE SERVER MINTING IT. The worker is
// deliberately NOT in the critical path of page loads (`wrangler.jsonc`
// `run_worker_first` names only these few API paths; every other request goes
// straight to the static bundle), so it never sees the landing and cannot mint
// anything. And `recordLanding` resolves the lead id SYNCHRONOUSLY inside a
// `useState` initializer so the id is on the very first event emitted — an
// async round-trip to LEARN the id would race that and could swap ids
// mid-session. So the client resolves, and this route only PERSISTS: it is a
// durability mechanism, never the source of truth.
//
// NOT `HttpOnly`, on purpose and unlike the session cookie: the client cascade
// in `attribution.ts` has to read this synchronously on a later visit, before
// any network call, for exactly the reason above. It is not a credential — it
// carries an opaque correlation id and an ad-platform click id, both of which
// the same-origin page already holds — and it is host-only with `SameSite=Lax`,
// so no cross-site context can read or send it.

import { logWorkerEvent } from "./log";

/** Durable lead id — the marketing join key on every event this reader emits. */
export const VISITOR_COOKIE = "retells-visitor";

/** Durable last-touch ad click id (`source:value:at`, see `encodeClickCookie`). */
export const CLICK_COOKIE = "retells-click";

/**
 * The route itself. Exported so the client posts to a name rather than a
 * hand-typed path that could drift from the `run_worker_first` entry.
 */
export const VISITOR_ENDPOINT = "/api/visitor";

/**
 * Cookie lifetime. Sized to the offline-conversion window the click id is kept
 * for at all (`MAX_CLICK_AGE_MS` in `apps/retells/src/lib/attribution.ts` and
 * `convex/model/attribution.ts`, 90 days, with the upload job's own 89-day
 * deadline just inside it). Refreshed on every page load, so this is 90 days of
 * inactivity, not 90 days from first contact.
 */
export const VISITOR_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

/**
 * Path `/`, NOT the `/auth` of every other cookie here. That is why this file
 * builds its own header instead of reusing `cookies.ts`'s `serialize`, which is
 * module-private and pins `Path=COOKIE_PATH`: the session JWT is deliberately
 * scoped so it never rides a static-asset request, whereas this one must be
 * readable by the page on every route the reader can land on.
 */
const VISITOR_COOKIE_PATH = "/";

/** Cap on the body: four short scalars, never a batch. */
const MAX_BODY_BYTES = 2 * 1024;

/** Lead ids — the worker's `SAFE_ID` shape (`api-log.ts`), duplicated here so
 *  this route stays dependency-free (no Zod) for a four-field payload. */
const LEAD_ID_PATTERN = /^[a-zA-Z0-9_-]{1,100}$/;

/**
 * Click-id shape. Mirrors `CLICK_ID_PATTERN` in `apps/retells/src/lib/attribution.ts`
 * and `convex/model/attribution.ts` — looser than a lead id in the two ways the
 * platforms force (`.` for Meta's dotted `fbclid`, 256 chars for a long
 * `gclid`). Enforced by REJECTING, never truncating: a sliced click id is a
 * well-formed lie that uploads cleanly and matches no click.
 */
const CLICK_ID_PATTERN = /^[a-zA-Z0-9_.-]{1,256}$/;

/** The param name a click id arrived under (`gclid`, `msclkid`, `li_fat_id`…).
 *  Shape only — this route does not carry the platform allowlist, because the
 *  value it echoes back goes to the same browser that sent it and is re-checked
 *  against `KNOWN_CLICK_SOURCES` server-side at checkout. */
const CLICK_SOURCE_PATTERN = /^[a-z_]{1,32}$/;

/** Clock-skew allowance on a client-stamped click time. */
const FUTURE_SKEW_MS = 24 * 60 * 60 * 1000;

/** One ad-platform click, structurally identical to the client's
 *  `StoredClickId` so the two sides pass it without a cast. */
export interface VisitorClick {
  source: string;
  value: string;
  at: number;
}

/** A validated `POST /api/visitor` body. */
export interface VisitorPayload {
  leadId: string;
  click: VisitorClick | null;
}

/**
 * `source:value:at` — three fields in one cookie, unencoded.
 *
 * Safe without escaping BECAUSE of the patterns above, not by luck: `:` is a
 * legal cookie-octet (RFC 6265 excludes CTLs, whitespace, `"`, `,`, `;` and
 * `\`) and appears in none of the three alphabets, so a split on `:` into
 * exactly three parts is unambiguous. One cookie rather than three keeps the
 * trio atomic — a `gclid` value filed under `msclkid` uploads to the wrong
 * platform, and a timestamp from a different landing puts the conversion
 * outside its click's import window.
 */
export function encodeClickCookie(click: VisitorClick): string {
  return `${click.source}:${click.value}:${click.at}`;
}

/**
 * Decode a click cookie, or `null` for anything that isn't one.
 *
 * Untrusted input at both ends — the reader can edit their own cookie jar, and
 * on the client this parses whatever `document.cookie` hands over — so it is
 * total: every malformed shape returns `null` and nothing throws. The same
 * patterns that make the encoding unambiguous are re-applied here rather than
 * assumed, because the value may have been round-tripped through a browser and
 * back since it was written.
 */
export function decodeClickCookie(
  raw: string | undefined | null,
): VisitorClick | null {
  if (!raw) return null;
  const parts = raw.split(":");
  if (parts.length !== 3) return null;
  const [source, value, rawAt] = parts;
  if (source === undefined || value === undefined || rawAt === undefined) {
    return null;
  }
  if (!CLICK_SOURCE_PATTERN.test(source)) return null;
  if (!CLICK_ID_PATTERN.test(value)) return null;
  // `Number` not `parseInt`: `parseInt("17e5junk")` happily answers 17, which
  // would date the click to 1970 and put it outside every upload window.
  const at = Number(rawAt);
  if (!Number.isSafeInteger(at) || at <= 0) return null;
  return { source, value, at };
}

/** `Set-Cookie` for the visitor pair: first-party, host-only (no `Domain`),
 *  `SameSite=Lax`, and NOT `HttpOnly` (see the header note). `Secure` is
 *  omitted only for `http://localhost` dev, where browsers exempt it.
 *
 *  Exported for the one legitimate script-side writer: `attribution.ts`
 *  mirrors a freshly adopted lead id into `document.cookie` with exactly this
 *  serialization, so the cookie tier of its cascade agrees with `localStorage`
 *  even when the fire-and-forget POST here never lands. The script-set copy
 *  lives on WebKit's shorter leash; this route's `Set-Cookie` remains the
 *  durable one. */
export function serializeVisitorCookie(
  name: string,
  value: string,
  maxAge: number,
  secure: boolean,
): string {
  const attrs = [
    `${name}=${value}`,
    `Path=${VISITOR_COOKIE_PATH}`,
    "SameSite=Lax",
    `Max-Age=${maxAge}`,
  ];
  if (secure) attrs.push("Secure");
  return attrs.join("; ");
}

/**
 * How long the click cookie should live: the remainder of its own 90-day
 * window, not a fresh 90 days. A click refreshed to full life on every page
 * load would outlive the window it can be uploaded in, and the client would
 * then keep reading an id that every platform has already stopped accepting.
 * A click already past the window yields 0 — an explicit clear, which is the
 * right answer: a click we can no longer report is worse than none, because the
 * sale gets stamped as reported.
 */
export function clickMaxAge(at: number, nowMs: number): number {
  const ageSeconds = Math.floor((nowMs - at) / 1000);
  if (ageSeconds < 0) return VISITOR_MAX_AGE_SECONDS;
  return Math.max(0, VISITOR_MAX_AGE_SECONDS - ageSeconds);
}

/** The `Set-Cookie` headers a validated payload produces. */
export function buildVisitorCookies(
  payload: VisitorPayload,
  secure: boolean,
  nowMs: number,
): string[] {
  const cookies = [
    serializeVisitorCookie(
      VISITOR_COOKIE,
      payload.leadId,
      VISITOR_MAX_AGE_SECONDS,
      secure,
    ),
  ];
  if (payload.click) {
    cookies.push(
      serializeVisitorCookie(
        CLICK_COOKIE,
        encodeClickCookie(payload.click),
        clickMaxAge(payload.click.at, nowMs),
        secure,
      ),
    );
  }
  return cookies;
}

/**
 * Validate a parsed body. Same posture as `api-log.ts`: unauthenticated route,
 * so nothing is trusted, every branch returns a value, and a partially-bad
 * payload is DEGRADED rather than rejected — a click that fails its checks
 * drops the click alone and still persists the lead id, because losing the
 * marketing join key over a malformed timestamp would cost more than the click.
 * Only a missing or malformed `leadId` fails the whole request, since it is the
 * only thing this route exists to store.
 */
export function parseVisitorPayload(
  body: unknown,
  nowMs: number,
): VisitorPayload | null {
  if (typeof body !== "object" || body === null || Array.isArray(body)) {
    return null;
  }
  const raw = body as Record<string, unknown>;
  const leadId = raw.leadId;
  if (typeof leadId !== "string" || !LEAD_ID_PATTERN.test(leadId)) return null;

  const value = raw.clickId;
  const source = raw.clickSource;
  const at = raw.clickAt;
  const clickOk =
    typeof value === "string" &&
    CLICK_ID_PATTERN.test(value) &&
    typeof source === "string" &&
    CLICK_SOURCE_PATTERN.test(source) &&
    typeof at === "number" &&
    Number.isSafeInteger(at) &&
    at > 0 &&
    // A client clock running fast would otherwise pin a click at a time no
    // upload window has opened for yet, and the cookie would outlive its own
    // reportable life by however far the clock is out.
    at <= nowMs + FUTURE_SKEW_MS;

  return {
    leadId,
    click: clickOk ? { source, value, at } : null,
  };
}

function json(status: number, body: unknown): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "content-type": "application/json; charset=utf-8" },
  });
}

/**
 * The request body as text, or `null` once it exceeds `maxBytes` — at which
 * point the stream is cancelled rather than drained. A missing body reads as
 * `""`, which the JSON parse downstream refuses on its own.
 */
async function readBodyBounded(
  request: Request,
  maxBytes: number,
): Promise<string | null> {
  const reader = request.body?.getReader();
  if (!reader) return "";
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    received += value.byteLength;
    if (received > maxBytes) {
      await reader.cancel();
      return null;
    }
    chunks.push(value);
  }
  const merged = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return new TextDecoder().decode(merged);
}

/**
 * `POST /api/visitor` — persist the client-resolved attribution ids as
 * first-party, server-set cookies (204 + `Set-Cookie`).
 *
 * The `sec-fetch-site` gate is the same one `handleSignOut` uses, and it
 * matters for the same reason the `/auth/callback` fetch-metadata check does:
 * `SameSite=Lax` governs when a cookie is SENT, not when one may be SET, so a
 * cross-site page's `no-cors` POST would otherwise land a `Set-Cookie` here
 * that the attacker's JS cannot read but the victim's browser keeps — pinning
 * an attacker-chosen lead id, or attaching an attacker's `gclid` to the
 * victim's next purchase. Our own caller is always same-origin. Absent
 * fetch-metadata is allowed, so a browser too old to send it still gets
 * durable attribution — WHICH IS WHY THE `Origin` CHECK BELOW EXISTS TOO. The
 * browsers old enough to omit `sec-fetch-site` (Safari ≤16.3 — precisely the
 * ITP population this cookie serves) are the ones the allowance would
 * otherwise leave open to that cross-site POST, and every one of them has
 * sent `Origin` on cross-origin POSTs since ~Safari 12. A present-and-foreign
 * `Origin` is refused; an absent one (old same-origin form/fetch shapes) is
 * allowed, so the two headers together cover each other's gaps.
 *
 * Never throws: every failure path returns a value, so a route that shares a
 * worker with sign-in cannot take it down.
 *
 * Rejections here log at `info`: this is an unauthenticated internet endpoint,
 * and internet scanners probing it are weather, not signal — `error` must stay
 * reserved for things an operator should act on (`logging-strategy`).
 */
export async function handleVisitor(
  request: Request,
  secure: boolean,
): Promise<Response> {
  if (request.method !== "POST") {
    return json(405, { error: "method_not_allowed" });
  }

  const site = request.headers.get("sec-fetch-site");
  if (site !== null && site !== "same-origin" && site !== "none") {
    logWorkerEvent("acquisition.visitor_cross_site", {}, "info");
    return json(403, { error: "cross_site" });
  }

  const origin = request.headers.get("origin");
  if (origin !== null && origin !== new URL(request.url).origin) {
    logWorkerEvent("acquisition.visitor_cross_site", {}, "info");
    return json(403, { error: "cross_site" });
  }

  // Cheap pre-read guard by declared length, before reading anything.
  const declared = Number(request.headers.get("content-length"));
  if (Number.isFinite(declared) && declared > MAX_BODY_BYTES) {
    return json(413, { error: "payload_too_large" });
  }

  // Read incrementally against the byte cap rather than buffering first: a
  // chunked body carries no `Content-Length` for the guard above, and
  // `request.text()` would buffer the whole thing before any check ran. This
  // also counts BYTES, which is what the cap names — `text.length` counted
  // UTF-16 code units and quietly let a multibyte body run ~3× over.
  const text = await readBodyBounded(request, MAX_BODY_BYTES);
  if (text === null) {
    return json(413, { error: "payload_too_large" });
  }

  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    return json(400, { error: "invalid_json" });
  }

  const nowMs = Date.now();
  const payload = parseVisitorPayload(body, nowMs);
  if (!payload) {
    // Count only — never echo the rejected payload (logging-strategy §4/§11),
    // and the lead id is a correlation field, not `data` fodder.
    logWorkerEvent("acquisition.visitor_invalid", {}, "info");
    return json(400, { error: "invalid_payload" });
  }

  const headers = new Headers({ "Cache-Control": "no-store" });
  for (const cookie of buildVisitorCookies(payload, secure, nowMs)) {
    headers.append("Set-Cookie", cookie);
  }
  return new Response(null, { status: 204, headers });
}
